import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import Layout from '../components/layout/Layout';
import FaqAccordion from '../components/FaqAccordion';
import { sendCollectionInquiry } from '../services/contactService';
import { theme, media } from '../styles/theme';
import {
  PageContainer,
  Section,
  PageTitle,
  SectionTitle,
  SubTitle,
  Text,
  Card,
  Button,
  LinkButton,
  FormContainer,
  FormGroup,
  Label,
  Input,
  TextArea,
  TextLink,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell
} from '../styles/components';

// Component-specific styled components

// TextLink look for plain anchors (in-page #kontakt jumps and mailto)
const AnchorLink = styled.a`
  color: ${theme.colors.primary};
  text-decoration: none;
  font-weight: ${theme.fontWeights.medium};

  &:hover {
    text-decoration: underline;
  }
`;

// Pads the bottom on mobile so the sticky CTA never covers the last content
const CollectionsContainer = styled(PageContainer)`
  ${media.mobile} {
    padding-bottom: 5rem;
  }
`;

// The two ways to sell (collection vs. a few single cards), side by side
const ChoiceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.md};
  margin: ${theme.spacing.md} 0;

  ${media.mobile} {
    grid-template-columns: 1fr;
  }
`;

const ChoiceCard = styled(Card)`
  display: flex;
  flex-direction: column;

  ${Text} {
    flex: 1;
  }
`;

const ChoiceActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.sm};
`;

const FactList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs} ${theme.spacing.md};
  list-style: none;
  padding: 0;
  margin: 0 0 ${theme.spacing.md};
  color: ${theme.colors.text};
  font-weight: ${theme.fontWeights.medium};

  li::before {
    content: '✓ ';
    color: ${theme.colors.success};
  }
`;

const StepList = styled.ol`
  list-style: none;
  padding: 0;
  margin: 0 0 ${theme.spacing.md};
  counter-reset: step;
  display: grid;
  gap: ${theme.spacing.sm};
`;

const StepCard = styled.li`
  counter-increment: step;
  position: relative;
  padding: ${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.xs} 3.5rem;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};

  &::before {
    content: counter(step);
    position: absolute;
    left: ${theme.spacing.md};
    top: ${theme.spacing.md};
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    background-color: ${theme.colors.accent};
    color: ${theme.colors.white};
    font-weight: ${theme.fontWeights.semibold};
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const StepTitle = styled(SubTitle)`
  margin-bottom: ${theme.spacing.xs};
`;

// Offset for the sticky header when jumping to #kontakt
const ContactFormContainer = styled(FormContainer)`
  scroll-margin-top: 5rem;
  margin-bottom: ${theme.spacing.lg};

  ${Text}, a {
    color: ${theme.colors.white};
  }
`;

// Mobile-only bar that jumps to the form; hidden at the top and once the form is in view
const StickyCta = styled.div<{ $visible: boolean }>`
  display: none;

  ${media.mobile} {
    display: block;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 50;
    padding: ${theme.spacing.xs} 16px;
    background-color: ${theme.colors.white};
    border-top: 1px solid ${theme.colors.border};
    transform: translateY(${props => (props.$visible ? '0' : '100%')});
    transition: transform ${theme.transitions.fast};

    a {
      width: 100%;
    }
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin-bottom: ${theme.spacing.md};
`;

const RateTable = styled(Table)`
  min-width: 560px;
`;

const FileInputLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  transition: background-color ${theme.transitions.fast};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.white};

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileList = styled.ul`
  list-style: none;
  padding: 0;
  margin-top: ${theme.spacing.sm};
`;

const FileItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${theme.spacing.sm};
  background-color: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: ${theme.borderRadius.sm};
  margin-bottom: ${theme.spacing.sm};
  color: ${theme.colors.white};
`;

const RemoveButton = styled.button`
  background-color: ${theme.colors.danger};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  cursor: pointer;
  font-size: ${theme.fontSizes.md};
  
  &:hover {
    background-color: ${theme.colors.danger};
    opacity: 0.9;
  }
`;

const SubmitButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: ${theme.colors.accent};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.sm};
  font-weight: ${theme.fontWeights.medium};
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover {
    filter: brightness(0.9);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  background-color: rgba(45, 184, 107, 0.1);
  color: ${theme.colors.success};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  margin-top: ${theme.spacing.sm};
  text-align: center;
`;

const ErrorMessage = styled.div`
  background-color: rgba(231, 76, 60, 0.1);
  color: ${theme.colors.danger};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  margin-bottom: ${theme.spacing.sm};
`;

// Single source of truth for the visible FAQ section AND the FAQPage JSON-LD.
// answerText is the plain-text version emitted as schema; the optional answer
// node may add links/markup but must convey the same content, since Google
// requires structured data to match what's visible on the page.
interface CollectionFaqEntry {
  question: string;
  answerText: string;
  answer?: React.ReactNode;
}

const COLLECTION_FAQ: CollectionFaqEntry[] = [
  {
    question: 'Hva betaler Tryllepapp for Magic-kort?',
    answerText:
      'Vi betaler 60–75% av markedspris for kort med verdi over 2 €, avhengig av etterspørsel og lagerstatus, og opptil 80% for enkelte spesielt ettertraktede kort. Rares og Mythic Rares under 2 € kjøpes for 1 kr per stk, og Commons/Uncommons for 60 kr per 1000 stk.',
  },
  {
    question: 'Kan jeg selge enkeltkort, eller må jeg selge hele samlingen?',
    answerText:
      'Du kan selge begge deler. Enkeltkort selger du enklest via buylisten vår, der du søker opp kortene og ser prisen med en gang. Hele samlinger tar vi imot via skjemaet på denne siden eller på e-post.',
    answer: (
      <p>
        Du kan selge begge deler. Enkeltkort selger du enklest via{' '}
        <TextLink to="/buylist">buylisten vår</TextLink>, der du søker opp kortene
        og ser prisen med en gang. Hele samlinger tar vi imot via skjemaet på denne
        siden eller på e-post.
      </p>
    ),
  },
  {
    question: 'Hvor raskt får jeg betalt når jeg selger MTG-kort?',
    answerText:
      'Selger du enkeltkort via buylisten, utbetales beløpet til kontoen din innen 2 virkedager etter at kortene er mottatt og gradert, eller du kan velge 10% ekstra i butikkreditt. For samlinger utbetales beløpet når du har akseptert det bindende tilbudet.',
  },
  {
    question: 'Hvordan selger jeg MTG-samlingen min til Tryllepapp?',
    answerText:
      'Kontakt oss på samlinger@tryllepapp.no eller via skjemaet på denne siden med informasjon om samlingen din, gjerne med en CSV-liste fra Manabox eller Deckbox, eller noen bilder. Vi gir et estimat, og for et bindende tilbud må vi se kortene.',
    answer: (
      <p>
        Kontakt oss på{' '}
        <a href="mailto:samlinger@tryllepapp.no">samlinger@tryllepapp.no</a>{' '}
        eller via skjemaet på denne siden med informasjon om samlingen din,
        gjerne med en CSV-liste fra Manabox eller Deckbox, eller noen bilder.
        Vi gir et estimat, og for et bindende tilbud må vi se kortene.
      </p>
    ),
  },
  {
    question: 'Henter Tryllepapp MTG-samlinger i hele Norge?',
    answerText:
      'Ja, større samlinger henter vi hvor som helst i landet. Mindre samlinger kan du sende til oss.',
  },
  {
    question: 'Hvordan bestemmes markedsprisen på MTG-kort?',
    answerText:
      'Vi bruker hovedsakelig laveste pris på Cardmarket for kort i tilsvarende tilstand, og for nyere kort kan trendpris benyttes. Kort som er vanskeligere å prissette, som dyre, eldre eller signerte kort, priser vi ved å se hva de faktisk omsettes for på plattformer som eBay, Discord og Facebook-grupper. Siden vi sammenligner med kort i den tilstanden dine kort faktisk er i, er tilstanden allerede priset inn, og vi gjør ikke noe ekstra tilstandstrekk oppå.',
  },
  {
    question: 'Hvor lang tid tar vurderingen?',
    answerText:
      'For mindre samlinger gir vi et bindende tilbud innen 1 virkedag etter mottak. For store samlinger kan det ta inntil 2 uker. Ved aksept utbetales beløpet til kontoen din.',
  },
  {
    question: 'Hva skjer om jeg takker nei til tilbudet?',
    answerText:
      'Før du sender samlingen, eller vi henter den, skriver vi en kontrakt med estimert pris — slik at både du og vi kjenner rammene på forhånd, og det sjelden blir aktuelt å takke nei. Velger du likevel å avslå det bindende tilbudet, belastes du for returporto og en sum for vurderingen, avhengig av omfang.',
  },
  {
    question: 'Må samlingen være sortert før dere vurderer den?',
    answerText:
      'Nei, usortert er helt greit. En CSV-liste eller noen bilder gjør vurderingen raskere og mer presis, men det er ikke et krav.',
  },
  {
    question: 'Kjøper dere skadde eller slitte kort?',
    answerText:
      'Ja. Kort med mye slitasje eller skade prises basert på tilstanden de er i.',
  },
  {
    question: 'Kjøper dere forseglede MTG-produkter?',
    answerText:
      'Ja, vi gir 75–90% av laveste pris for tilsvarende produkt på Cardmarket. Dette gjelder engelske produkter produsert etter 2015. Eldre forseglede produkter kjøper vi også, til andre satser — ta kontakt for mer info.',
  },
  {
    question: 'Hva får jeg for bulk og vanlige kort?',
    answerText:
      'Rares og Mythic Rares med verdi under 2 € kjøpes for 1 kr per stk, og Commons/Uncommons for 60 kr per 1000 stk.',
  },
];

interface CollectionFormData {
  name: string;
  email: string;
  message: string;
  subject: string;
  files?: File[];
}

interface FileUpload {
  file: File;
  preview?: string;
}

const CollectionsPage: React.FC = () => {
  const [formData, setFormData] = useState<CollectionFormData>({
    name: '',
    email: '',
    message: '',
    subject: "Samlingskjøp henvendelse"   
  });
  const [files, setFiles] = useState<FileUpload[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const choicesRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const [choicesInView, setChoicesInView] = useState(true);
  const [contactInView, setContactInView] = useState(false);

  // The sticky mobile CTA only shows while neither the top choice cards
  // nor the contact form are on screen
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === choicesRef.current) setChoicesInView(entry.isIntersecting);
        if (entry.target === contactRef.current) setContactInView(entry.isIntersecting);
      });
    });
    if (choicesRef.current) observer.observe(choicesRef.current);
    if (contactRef.current) observer.observe(contactRef.current);
    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;
    
    const newFiles = Array.from(selectedFiles).map(file => {
      // Create a preview URL for images
      let preview;
      if (file.type.startsWith('image/')) {
        preview = URL.createObjectURL(file);
      }
      return { file, preview };
    });
    
    setFiles(prev => [...prev, ...newFiles]);
    
    // Clear the input field so the same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const removeFile = (index: number) => {
    setFiles(prev => {
      const newFiles = [...prev];
      // If there's a preview URL, revoke it to free up memory
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview!);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess(false);
    setSubmitError('');
    
    try {
      // Convert FileUpload[] to File[] for the API
      const fileList = files.length > 0 
        ? files.map(fileObj => fileObj.file)
        : undefined;
      
      // Send form data with or without files using contactService
      await sendCollectionInquiry({
        ...formData,
        files: fileList
      });
      
      // Clean up any preview URLs
      files.forEach(fileObj => {
        if (fileObj.preview) {
          URL.revokeObjectURL(fileObj.preview);
        }
      });
      
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        message: '',
        subject: "Samlingskjøp henvendelse"
      });
      setFiles([]);
    } catch (error) {
      console.error('Failed to send message', error);
      setSubmitError('Det oppstod en feil ved sending av skjema. Vennligst prøv igjen senere.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Layout>
      <Helmet>
        <title>Selge MTG-kort? Vi kjøper Magic-kort og hele samlinger | Tryllepapp</title>
        <meta name="description" content="Selge MTG-kort i Norge? Tryllepapp kjøper Magic-kort – enkeltkort og hele samlinger. 60–75% av markedspris, rask utbetaling, og vi henter store samlinger i hele landet." />
        <link rel="canonical" href="https://selg.tryllepapp.no/selge-samling" />
        <meta property="og:title" content="Selge MTG-kort? Vi kjøper Magic-kort og hele samlinger | Tryllepapp" />
        <meta property="og:description" content="Selge MTG-kort? Vi kjøper Magic-kort – enkeltkort og hele samlinger. 60–75% av markedspris, rask utbetaling." />
        <meta property="og:url" content="https://selg.tryllepapp.no/selge-samling" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            // Same @id as the sitewide Organization node in public/index.html —
            // crawlers merge the two, so org details live only in the template.
            '@type': 'OnlineStore',
            '@id': 'https://selg.tryllepapp.no/#organization',
            makesOffer: {
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: 'Kjøp av MTG-kort og samlinger',
                description: 'Vi kjøper Magic: The Gathering-kort – enkeltkort og hele samlinger. Vi henter store samlinger hvor som helst i Norge. 60–75% av markedspris.',
              },
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: COLLECTION_FAQ.map(({ question, answerText }) => ({
              '@type': 'Question',
              name: question,
              acceptedAnswer: { '@type': 'Answer', text: answerText },
            })),
          })}
        </script>
      </Helmet>
      <CollectionsContainer>
        <PageTitle>Selge MTG-kort? Vi kjøper Magic-kort og hele samlinger</PageTitle>

      <Section>
        <Text>
          Tryllepapp kjøper <u><b>ALLE</b></u> Magic: The Gathering-kort (MTG). Vil du selge MTG-kort,
          enten det er noen få enkeltkort eller en hel samling som har ligget i skapet i årevis?
          Vi gir et konkurransedyktig tilbud basert på tilstand, og utbetaler raskt til kontoen din.
        </Text>
        <Text>
          Å selge Magic-kort til oss er enkelt: Enkeltkort søker du opp i{' '}
          <TextLink to="/buylist">buylisten</TextLink> og ser prisen med en gang. Har du mange kort eller
          en hel MTG-samling, tar du kontakt via <AnchorLink href="#kontakt">skjemaet</AnchorLink> — så gjør vi jobben med å prise alt.
        </Text>

        <ChoiceGrid ref={choicesRef}>
          <ChoiceCard>
            <SubTitle as="h2">Har du en samling?</SubTitle>
            <Text>
              Fortell oss litt om samlingen, gjerne med en CSV-liste eller noen bilder, så gir vi deg et
              tilbud. Vi gjør jobben med å prise alt for deg.
            </Text>
            <ChoiceActions>
              <Button as="a" href="#kontakt" variant="primary" size="large">
                Få tilbud på samlingen
              </Button>
            </ChoiceActions>
          </ChoiceCard>
          <ChoiceCard>
            <SubTitle as="h2">Selge enkeltkort? Bruk buylisten</SubTitle>
            <Text>
              Har du bare noen få MTG-kort du vil selge, trenger du ikke kontakte oss. I buylisten søker du opp
              kortene, ser nøyaktig hva vi betaler, og sender dem inn. Utbetaling skjer innen 2 virkedager etter
              at kortene er mottatt og gradert, eller du kan velge 10% ekstra i butikkreditt.
            </Text>
            <ChoiceActions>
              <LinkButton to="/buylist" variant="secondary" size="large">
                Gå til buylisten
              </LinkButton>
            </ChoiceActions>
          </ChoiceCard>
        </ChoiceGrid>

        <FactList>
          <li>60–75% av markedspris</li>
          <li>Tilbud innen 1 virkedag (små samlinger)</li>
          <li>Vi henter i hele Norge</li>
          <li>Usortert er helt greit</li>
        </FactList>

        <SectionTitle>Slik selger du Magic-kort til oss</SectionTitle>
        <StepList>
          <StepCard>
            <StepTitle as="h3">Kontakt oss</StepTitle>
            <Text>
              Send oss en e-post på <AnchorLink href="mailto:samlinger@tryllepapp.no">samlinger@tryllepapp.no</AnchorLink> eller
              fyll ut <AnchorLink href="#kontakt">skjemaet</AnchorLink> med informasjon om din MTG-samling. Jo mer informasjon du
              kan gi, desto bedre. En liste på *.csv format er å foretrekke. Flere av de mest populære
              applikasjonene for organisering slik som Manabox appen eller Deckbox nettsiden har funksjonalitet
              for å eksportere samlingen din til *.csv format.
            </Text>
            <Text>
              Dersom du ikke har en liste, send oss gjerne noen bilder så vi får vurdert om dette er noe vi kan kjøpe.
            </Text>
          </StepCard>
          <StepCard>
            <StepTitle as="h3">Vurdering</StepTitle>
            <Text>
              Basert på informasjonen du gir, kan vi gi et grovt estimat.
              <strong> Dette er ikke et bindende tilbud.</strong> For at vi kan gi et bindende tilbud må kortene
              beskues for å vurdere tilstanden da dette kan ha stor innvirkning på prisen.
            </Text>
            <Text>
              Samlingen sendes til oss, eller <strong>dersom det er en større samling så vil vi hente hvor som
              helst i landet.</strong> Før sending eller oppmøte skriver vi en kontrakt som inneholder estimert pris
              for samlingen og en forpliktelse om å selge innenfor rammeverket slik det er beskrevet på denne siden.
              Dette slik at det er trygghet for både kjøper og selger.
            </Text>
          </StepCard>
          <StepCard>
            <StepTitle as="h3">Tilbud</StepTitle>
            <Text>
              Etter mottak av samlingen vil vi gi deg et bindende tilbud — for mindre samlinger innen{' '}
              <strong>1 virkedag</strong>, for store samlinger senest i løpet av 2 uker fra mottak. Dersom dette
              tilbudet ikke aksepteres vil vi belaste deg for returporto og en sum for vurderingen, avhengig av
              omfang. Vedlagt tilbudet kan det gis en fullstendig liste over alle kortene som inngår med pris per
              enhet så det blir en nøyaktig og gjennomsiktig prosess.
            </Text>
          </StepCard>
          <StepCard>
            <StepTitle as="h3">Utbetaling</StepTitle>
            <Text>Ved aksept av tilbudet utbetales beløpet til din konto.</Text>
          </StepCard>
        </StepList>

        <SectionTitle>Hva betaler vi for Magic-kort?</SectionTitle>
        <Text>
          Vi betaler <strong>opptil 80% av markedspris</strong> for de mest ettertraktede kortene,
          og <strong>60–75%</strong> for enkeltkort med markedsverdi over 2 € — avhengig av vårt
          nåværende lager og hvor ettertraktet kortet er. Satsene i korte trekk:
        </Text>
        <TableWrapper>
          <RateTable>
            <TableHead>
              <TableRow>
                <TableHeader>Hva</TableHeader>
                <TableHeader>Sats</TableHeader>
                <TableHeader>Prisgrunnlag</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Utvalgte ettertraktede kort (se listen under)</TableCell>
                <TableCell><strong>80%</strong></TableCell>
                <TableCell>Markedspris (eBay, Discord, salgsgrupper)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Enkeltkort med verdi over 2 €</TableCell>
                <TableCell><strong>Inntil 75%</strong> (60–75%, avhengig av lager og etterspørsel)</TableCell>
                <TableCell>Cardmarket laveste i samme tilstand (trendpris for nyere kort)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Rares og Mythic Rares under 2 €</TableCell>
                <TableCell>1 kr per stk</TableCell>
                <TableCell>—</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Commons og Uncommons under 2 €</TableCell>
                <TableCell>60 kr per 1000 stk</TableCell>
                <TableCell>—</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Forseglede produkter (engelsk, produsert etter 2015)</TableCell>
                <TableCell><strong>75–90%</strong></TableCell>
                <TableCell>Cardmarket laveste</TableCell>
              </TableRow>
            </TableBody>
          </RateTable>
        </TableWrapper>
        <Text as="div">
          <p>Kortene vi for øyeblikket betaler <strong>høyeste sats på 80%</strong> for:</p>
          <ul>
            <li>Mishra's Workshop</li>
            <li>Power 9 fra Unlimited</li>
            <li>Power 9 fra CE/IE (unntatt Time Walk)</li>
            <li>Dobbeltland fra Unlimited (unntatt Bayou og Taiga)</li>
            <li>Library of Alexandria</li>
            <li>Juzam Djinn</li>
          </ul>
        </Text>
        <Text>
          Eldre forseglede produkter kjøper vi også, til andre satser — ta kontakt for mer info!
          Du kan lese mer om <TextLink to="/blogg/hva-betaler-vi-for-magic-kort">hva vi betaler for Magic-kort</TextLink> på bloggen.
        </Text>

        <SectionTitle as="h3">Arvet en Magic-samling?</SectionTitle>
        <Text>
          Du trenger ikke vite hva samlingen er verdt — eller hva kortene heter — for å kontakte oss.
          Send noen bilder, så vurderer vi den for deg. Usortert er helt greit.
          Usikker på om kortene har verdi? <TextLink to="/blogg/er-magic-kort-verdt">Finn ut om gamle Magic-kort er verdt noe</TextLink>.
        </Text>

        <SectionTitle as="h3">Gamle kort fra 90-tallet?</SectionTitle>
        <Text>
          De eldste settene er ofte de mest verdifulle — flere av kortene på 80%-listen vår er fra
          Magics aller første år. Les mer om <TextLink to="/blogg/er-magic-kort-verdt">hvilke gamle MTG-kort som er verdt mest</TextLink>.
        </Text>

        <SectionTitle>Hvordan bestemmes markedsprisen på MTG-kort?</SectionTitle>
        <Text>
          For å bestemme markedspris benyttes hovedsakelig laveste pris på Cardmarket for kort i tilsvarende tilstand, for nyere kort kan trendpris benyttes.
          For kort som er vanskeligere å prissette slik som meget dyre kort, eldre kort, signerte kort osv. benytter vi plattformer som eBay, Discord og Facebook-grupper hvor vi kan se hva kortene faktisk omsettes for.
        </Text>
        <Text>
          Når vi vurderer en samling, sammenligner vi altså med prisen på kort i den tilstanden dine kort faktisk er i.
          Tilstanden er dermed allerede priset inn, og vi gjør ikke noe ekstra tilstandstrekk oppå — det er slik den automatiske buylisten fungerer,
          hvor alle priser oppgis for NM. Vi har skrevet mer utfyllende om <TextLink to="/blogg/slik-priser-vi-mtg-kort">hvorfor gamle og nye kort prises ulikt</TextLink> på bloggen.
        </Text>
      </Section>

      <ContactFormContainer id="kontakt" ref={contactRef}>
        <SectionTitle>Kontakt oss angående din samling</SectionTitle>
        <Text>
          Du kan også sende e-post til{' '}
          <a href="mailto:samlinger@tryllepapp.no">samlinger@tryllepapp.no</a>.
        </Text>
        <Text>
          Møter du oss på stand? Fyll ut <TextLink to="/selge-pa-stand">skjemaet for salg på stand</TextLink>.
        </Text>

        {submitSuccess && (
          <SuccessMessage>
            Takk for din henvendelse! Vi vil kontakte deg så snart som mulig.
          </SuccessMessage>
        )}
        
        {submitError && (
          <ErrorMessage>
            {submitError}
          </ErrorMessage>
        )}
        
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Navn</Label>
            <Input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="email">E-post</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="message">Beskrivelse av samling</Label>
            <TextArea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              placeholder="Beskriv din kortsamling (ca. antall kort, hvilke sett, sjeldne kort, tilstand osv.)"
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Last opp filer (CSV-filer eller bilder)</Label>
            <FileInputLabel>
              📁 Velg filer
              <FileInput 
                ref={fileInputRef}
                type="file" 
                multiple 
                accept=".csv,image/*" 
                onChange={handleFileChange} 
              />
            </FileInputLabel>
            
            {files.length > 0 && (
              <FileList>
                {files.map((file, index) => (
                  <FileItem key={index}>
                    <div>
                      <strong>{file.file.name}</strong> ({(file.file.size / 1024).toFixed(1)} KB)
                      {file.preview && (
                        <div>
                          <img 
                            src={file.preview} 
                            alt="Preview" 
                            style={{ 
                              height: '50px', 
                              marginTop: '0.5rem', 
                              borderRadius: theme.borderRadius.sm
                            }} 
                          />
                        </div>
                      )}
                    </div>
                    <RemoveButton type="button" onClick={() => removeFile(index)}>
                      Fjern
                    </RemoveButton>
                  </FileItem>
                ))}
              </FileList>
            )}
          </FormGroup>
          
          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sender...' : 'Send henvendelse'}
          </SubmitButton>
        </form>
      </ContactFormContainer>

      <Section>
        <SectionTitle>Ofte stilte spørsmål om å selge Magic-kort og samlinger</SectionTitle>
        <FaqAccordion
          items={COLLECTION_FAQ.map(({ question, answer, answerText }) => ({
            question,
            answer: answer ?? <p>{answerText}</p>,
          }))}
        />
      </Section>
      </CollectionsContainer>

      <StickyCta $visible={!choicesInView && !contactInView}>
        <Button as="a" href="#kontakt" variant="primary" size="large">
          Få tilbud på samlingen
        </Button>
      </StickyCta>
    </Layout>
  );
};

export default CollectionsPage;
